import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BusinessIcon from "@mui/icons-material/Business";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import GroupIcon from "@mui/icons-material/Group";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useMemo } from "react";
import { useLazyLoadQuery } from "react-relay";
import { useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { InternDashboardQuery } from "./__generated__/InternDashboardQuery.graphql";
import { useColorMode } from "../../contexts/ColorModeContext";
import formatNumber from "../../utils/formatNumber";
import InternJobsHealthCard from "./overview/InternJobsHealthCard";
import InternPushStatusCard from "./overview/InternPushStatusCard";

const query = graphql`
  query InternDashboardQuery {
    internPlatformMetrics {
      totalBrands
      activeBrands
      totalAppUsers
      totalAppSessions30d
      totalAppSales30d
      totalAppOrders30d
      brandStatusCounts {
        status
        count
        label
      }
      trends {
        date
        appSessions
        appSales
        appOrders
        newAppUsers
      }
      alerts {
        alertType
        title
        message
        count
        severity
        brandIds
      }
    }
    internPushNotificationStatus {
      ...InternPushStatusCard_status
    }
    internJobHealthMetrics {
      ...InternJobsHealthCard_metrics
    }
  }
`;

// Status colors for pie chart (keys match AppBuildState values from backend)
const STATUS_COLORS: Record<string, string> = {
  production: "#10b981", // green
  pre_production: "#3b82f6", // blue
  pending_kinn_setup: "#ef4444", // red
  pending_brand_setup: "#f59e0b", // amber
  disabled: "#6b7280", // gray
};

// Stat card component
interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: number;
}

const StatCard = ({ title, value, subtitle, icon }: StatCardProps) => {
  const { colorMode } = useColorMode();

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        bgcolor: "var(--intern-bg-primary)",
        border: "1px solid var(--intern-border-default)",
        borderRadius: 2,
        height: "100%",
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box
          sx={{
            p: 1,
            borderRadius: 1.5,
            bgcolor:
              colorMode === "dark"
                ? "rgba(99, 102, 241, 0.15)"
                : "rgba(99, 102, 241, 0.1)",
            color: "#6366f1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              color: "var(--intern-text-secondary)",
              fontSize: "0.75rem",
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              mb: 0.5,
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: "var(--intern-text-primary)",
              fontWeight: 700,
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
              lineHeight: 1.2,
            }}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-tertiary)",
                fontSize: "0.6875rem",
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};

// Alert severity icon mapping
const getAlertIcon = (severity: string) => {
  switch (severity) {
    case "error":
      return <ErrorOutlineIcon fontSize="small" />;
    case "warning":
      return <WarningAmberIcon fontSize="small" />;
    default:
      return <InfoOutlinedIcon fontSize="small" />;
  }
};

const InternDashboard = () => {
  const data = useLazyLoadQuery<InternDashboardQuery>(query, {});
  const metrics = data.internPlatformMetrics;
  const pushStatus = data.internPushNotificationStatus;
  const jobHealthMetrics = data.internJobHealthMetrics;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { colorMode } = useColorMode();
  const navigate = useNavigate();

  // Prepare pie chart data
  const pieData = useMemo(() => {
    return (
      metrics?.brandStatusCounts?.map((item) => ({
        name: item.label,
        value: item.count,
        status: item.status,
      })) ?? []
    );
  }, [metrics?.brandStatusCounts]);

  // Prepare trend chart data
  const trendData = useMemo(() => {
    return (
      metrics?.trends?.map((item) => ({
        date: new Date(item.date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        sessions: item.appSessions,
        sales: item.appSales,
        orders: item.appOrders,
        newUsers: item.newAppUsers,
      })) ?? []
    );
  }, [metrics?.trends]);

  // Theme-aware colors
  const chartColors = {
    grid: colorMode === "dark" ? "#374151" : "#e5e7eb",
    text: colorMode === "dark" ? "#9ca3af" : "#6b7280",
    tooltip: {
      bg: colorMode === "dark" ? "#1f2937" : "#ffffff",
      border: colorMode === "dark" ? "#374151" : "#e5e7eb",
    },
  };

  if (!metrics) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={100} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={300} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography
          variant="h5"
          fontWeight={600}
          sx={{
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
            color: "var(--intern-text-primary)",
          }}
        >
          Platform Dashboard
        </Typography>
      </Stack>

      {/* Stats Grid */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Total Brands"
            value={metrics.totalBrands}
            subtitle={`${metrics.activeBrands} active`}
            icon={<BusinessIcon />}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title="App Users (30d)"
            value={formatNumber(metrics.totalAppUsers)}
            subtitle="New users acquired"
            icon={<GroupIcon />}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title="App Sales (30d)"
            value={formatNumber(metrics.totalAppSales30d, "currency")}
            subtitle={`${formatNumber(metrics.totalAppOrders30d)} orders`}
            icon={<AttachMoneyIcon />}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Sessions (30d)"
            value={formatNumber(metrics.totalAppSessions30d)}
            subtitle="Total app sessions"
            icon={<TouchAppIcon />}
          />
        </Grid>
      </Grid>

      {/* Alerts Section */}
      {metrics.alerts && metrics.alerts.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: "var(--intern-text-secondary)",
              fontWeight: 600,
              mb: 1.5,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontSize: "0.6875rem",
            }}
          >
            Alerts & Actions Required
          </Typography>
          <Stack spacing={1.5}>
            {metrics.alerts.map((alert, index) => (
              <Alert
                key={index}
                severity={alert.severity as "error" | "warning" | "info"}
                icon={getAlertIcon(alert.severity)}
                sx={{
                  bgcolor:
                    colorMode === "dark"
                      ? `rgba(${
                          alert.severity === "error"
                            ? "239, 68, 68"
                            : alert.severity === "warning"
                            ? "245, 158, 11"
                            : "59, 130, 246"
                        }, 0.1)`
                      : undefined,
                  border: "1px solid var(--intern-border-default)",
                  "& .MuiAlert-icon": {
                    color:
                      alert.severity === "error"
                        ? "#ef4444"
                        : alert.severity === "warning"
                        ? "#f59e0b"
                        : "#3b82f6",
                  },
                }}
                action={
                  <Chip
                    label={`${alert.count} brand${alert.count > 1 ? "s" : ""}`}
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      // Navigate to first affected brand
                      if (alert.brandIds && alert.brandIds.length > 0) {
                        navigate(
                          `/intern/brands/${alert.brandIds[0]}/overview`
                        );
                      }
                    }}
                  />
                }
              >
                <AlertTitle sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                  {alert.title}
                </AlertTitle>
                <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
                  {alert.message}
                </Typography>
              </Alert>
            ))}
          </Stack>
        </Box>
      )}

      {/* Charts Row */}
      <Grid container spacing={2}>
        {/* Brand Status Distribution */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5 },
              bgcolor: "var(--intern-bg-primary)",
              border: "1px solid var(--intern-border-default)",
              borderRadius: 2,
              height: "100%",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: "var(--intern-text-secondary)",
                fontWeight: 600,
                mb: 2,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                fontSize: "0.6875rem",
              }}
            >
              Brand Status Distribution
            </Typography>
            <Box sx={{ height: 280, position: "relative" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={isMobile ? 45 : 55}
                    outerRadius={isMobile ? 70 : 85}
                    paddingAngle={2}
                    dataKey="value"
                    stroke="none"
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={STATUS_COLORS[entry.status] || "#6b7280"}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: chartColors.tooltip.bg,
                      border: `1px solid ${chartColors.tooltip.border}`,
                      borderRadius: 8,
                      fontSize: "0.75rem",
                    }}
                    formatter={(value: number, name: string) => [value, name]}
                  />
                  <Legend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    iconType="circle"
                    iconSize={8}
                    formatter={(value) => (
                      <span
                        style={{
                          color: chartColors.text,
                          fontSize: "0.75rem",
                        }}
                      >
                        {value}
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Sessions Trend */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5 },
              bgcolor: "var(--intern-bg-primary)",
              border: "1px solid var(--intern-border-default)",
              borderRadius: 2,
              height: "100%",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: "var(--intern-text-secondary)",
                fontWeight: 600,
                mb: 2,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                fontSize: "0.6875rem",
              }}
            >
              Platform Activity (30 Days)
            </Typography>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={trendData}
                  margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="colorSessions"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient
                      id="colorNewUsers"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={chartColors.grid}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    stroke={chartColors.text}
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: chartColors.text }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    stroke={chartColors.text}
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: chartColors.text }}
                    tickFormatter={(value) => formatNumber(value)}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: chartColors.tooltip.bg,
                      border: `1px solid ${chartColors.tooltip.border}`,
                      borderRadius: 8,
                      fontSize: "0.75rem",
                    }}
                    formatter={(value: number, name: string) => [
                      formatNumber(value),
                      name === "sessions" ? "Sessions" : "New Users",
                    ]}
                    labelStyle={{ color: chartColors.text }}
                  />
                  <Legend
                    iconType="circle"
                    iconSize={8}
                    formatter={(value) => (
                      <span
                        style={{
                          color: chartColors.text,
                          fontSize: "0.75rem",
                        }}
                      >
                        {value === "sessions" ? "Sessions" : "New Users"}
                      </span>
                    )}
                  />
                  <Area
                    type="monotone"
                    dataKey="sessions"
                    stroke="#6366f1"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorSessions)"
                    isAnimationActive={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="newUsers"
                    stroke="#10b981"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorNewUsers)"
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Push Notifications Status */}
        <Grid item xs={12} md={6}>
          {pushStatus && <InternPushStatusCard status={pushStatus} />}
        </Grid>

        {/* Jobs Health */}
        <Grid item xs={12} md={6}>
          {jobHealthMetrics && (
            <InternJobsHealthCard metrics={jobHealthMetrics} />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default InternDashboard;
